<template>
  <div class="splash-screen fixed inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 flex items-center justify-center z-50">
    <!-- Background Pattern -->
    <div class="absolute inset-0 opacity-10">
      <div class="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
    </div>
    
    <!-- Main Content -->
    <div class="relative text-center text-white animate__animated animate__fadeIn">
      <!-- Logo/Icon -->
      <div class="mb-8">
        <div class="w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-4 animate__animated animate__zoomIn overflow-hidden" style="animation-delay: 0.2s">
          <img 
            src="/images/icon-192x192.png" 
            :alt="branding.appFullName + ' Logo'" 
            class="w-full h-full object-cover rounded-full"
          />
        </div>
        
        <!-- App Name -->
        <h1 class="text-4xl font-bold mb-2 animate__animated animate__slideInUp" style="animation-delay: 0.4s">
          {{ branding.appName }}
        </h1>
        <p class="text-lg opacity-90 animate__animated animate__slideInUp" style="animation-delay: 0.6s">
          {{ branding.appTagline }}
        </p>
      </div>
      
      <!-- Loading Animation -->
      <div class="animate__animated animate__fadeIn" style="animation-delay: 0.8s">
        <div class="flex justify-center items-center space-x-2 mb-4">
          <div class="w-2 h-2 bg-white rounded-full animate-bounce" style="animation-delay: 0s"></div>
          <div class="w-2 h-2 bg-white rounded-full animate-bounce" style="animation-delay: 0.1s"></div>
          <div class="w-2 h-2 bg-white rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
        </div>
        <p class="text-sm opacity-75">Loading your experience...</p>
      </div>
      
      <!-- Progress Bar -->
      <div class="mt-8 w-64 mx-auto">
        <div class="h-1 bg-white/20 rounded-full overflow-hidden">
          <div 
            class="h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full transition-all duration-100 ease-out shadow-sm"
            :style="{ width: progress + '%' }"
          ></div>
        </div>
        <div class="text-center mt-2 text-white/70 text-xs">
          {{ Math.round(progress) }}%
        </div>
      </div>
    </div>
    
    <!-- Version Info -->
    <div class="absolute bottom-12 left-1/2 transform -translate-x-1/2 text-white/60 text-sm animate__animated text-center animate__fadeIn" style="animation-delay: 1s">
      {{ branding.fullAppTitle }} <br> {{ branding.copyrightText }} <br>
      <span class="font-medium">{{ branding.developerName }}</span>
    </div>
    
   
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useBranding } from '@/composables/useBranding'

// Branding configuration
const branding = useBranding()

// Progress tracking
const progress = ref(0)

// Props to receive completion callback
const props = defineProps({
  onComplete: {
    type: Function,
    required: true
  }
})

onMounted(() => {
  // Animate progress bar over 5 seconds
  let currentProgress = 0
  const interval = setInterval(() => {
    currentProgress += 2 // Increment by 2% every 100ms = 5 seconds total
    progress.value = Math.min(currentProgress, 100)
    
    if (currentProgress >= 100) {
      clearInterval(interval)
      // Small delay after completion animation
      setTimeout(() => {
        props.onComplete()
      }, 300)
    }
  }, 100)
})
</script>

<style scoped>
/* Custom grid background */
.bg-grid-white\/\[0\.05\] {
  background-image: 
    linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
}

/* Enhanced bounce animation for loading dots */
@keyframes bounce {
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0,0,0);
  }
  40%, 43% {
    transform: translate3d(0, -8px, 0);
  }
  70% {
    transform: translate3d(0, -4px, 0);
  }
  90% {
    transform: translate3d(0, -2px, 0);
  }
}

.animate-bounce {
  animation: bounce 1.4s ease-in-out infinite;
}

/* Respect user's motion preferences */
@media (prefers-reduced-motion: reduce) {
  .animate-bounce {
    animation: none;
  }
  
  .animate__animated {
    animation-duration: 0.01ms !important;
    animation-delay: 0.01ms !important;
  }
}

/* Fade in animation */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.animate__fadeIn {
  animation: fadeIn 0.8s ease-out forwards;
}

.animate__zoomIn {
  animation: zoomIn 0.6s ease-out forwards;
}

.animate__slideInUp {
  animation: slideInUp 0.6s ease-out forwards;
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>